import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  Search,
  Filter,
  Edit3,
  Trash2,
  User,
  Home,
  FileText,
  Users,
  BarChart3,
  LogOut,
  ChevronDown,
  X,
  Save,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

const useOnClickOutside = (ref, handler) => {
  const handlerRef = React.useRef(handler);
  React.useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  React.useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handlerRef.current?.(e);
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref]);
};

/*****************
 * THEME (EM)
 *****************/
/*****************
 * THEME (Energy Matters – Green primary)
 *****************/
const ThemeProvider = ({ children }) => {
  React.useEffect(() => {
    const r = document.documentElement;

    // --- Brand palette (approx from site) ---
    r.style.setProperty("--em-primary", "#1F7A3E"); // EM green (primary)
    r.style.setProperty("--em-primary-700", "#176235"); // darker green
    r.style.setProperty("--em-primary-50", "#F2F8F4"); // very light green surface
    r.style.setProperty("--em-primary-100", "#DDEEE4"); // light border tint

    r.style.setProperty("--em-accent-orange", "#F36E21"); // EM orange (accent)
    r.style.setProperty("--em-accent-yellow", "#FFD84D"); // soft yellow accent

    r.style.setProperty("--em-dark", "#1B1F24"); // text
    r.style.setProperty("--em-light", "#F6F7F9"); // page bg

    // --- Bootstrap overrides ---
    r.style.setProperty("--bs-primary", "var(--em-primary)");
    r.style.setProperty("--bs-primary-rgb", "31, 122, 62");
    r.style.setProperty("--bs-link-color", "var(--em-primary)");
    r.style.setProperty("--bs-link-hover-color", "var(--em-primary-700)");
    r.style.setProperty("--bs-body-bg", "var(--em-light)");
    r.style.setProperty("--bs-body-color", "var(--em-dark)");
    r.style.setProperty("--bs-border-color", "var(--em-primary-100)");
  }, []);

  return (
    <>
      {/* Global EM styles */}
      <style>{`
        /* Panels / sections */
        .em-section{
          background: var(--em-primary-50);
          border: 1px solid var(--em-primary-100);
          border-radius: 12px;
          padding: 16px 16px 8px;
          margin-bottom: 16px;
          box-shadow: 0 1px 0 rgba(0,0,0,0.02);
          position: relative;
        }
        /* subtle top border bar for stronger separation */
        .em-section::before{
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          background: var(--em-primary);
          opacity: 0.25;
        }
        .em-legend{
          display:inline-block;
          padding: 6px 12px;
          background:#fff;
          border:1px solid var(--em-primary-100);
          border-radius:999px;
          font-weight:700;
          font-size:0.95rem;
          color: var(--em-dark);
        }
        .em-section .form-control,
        .em-section .form-select,
        .em-section textarea{
          background:#fff;
          border-color: var(--em-primary-100);
        }

        /* Buttons (green primary) */
        .btn-primary{
          --bs-btn-bg: var(--em-primary);
          --bs-btn-border-color: var(--em-primary);
          --bs-btn-hover-bg: var(--em-primary-700);
          --bs-btn-hover-border-color: var(--em-primary-700);
          --bs-btn-active-bg: var(--em-primary-700);
          --bs-btn-active-border-color: var(--em-primary-700);
        }
        .btn-outline-primary{
          --bs-btn-color: var(--em-primary);
          --bs-btn-border-color: var(--em-primary);
          --bs-btn-hover-bg: var(--em-primary);
          --bs-btn-hover-border-color: var(--em-primary);
          --bs-btn-hover-color: #fff;
        }

        /* Badges */
        .badge-em{
          background: rgba(31,122,62,.12);
          color: var(--em-primary);
          border: 1px solid var(--em-primary-100);
        }

        /* Navbar/Header */
        .em-header{
          background: var(--em-primary);
          color: #fff;
        }

        /* Sidebar active */
        .em-sidebar-active{
          background: var(--em-primary) !important;
          color: #fff !important;
        }

        /* Sticky thead bg (avoid bleed) */
        .table thead.table-light{
          background:#f3f4f6 !important;
        }

        /* Accent orange utility (use sparingly) */
        .text-accent-orange{ color: var(--em-accent-orange); }

        :root{
        --em-reject: #F36E21;  /* orange */
        --em-hold:   #FFD84D;  /* yellow */
        }
        .em-chip{
        display:inline-block; padding:2px 8px; border-radius:999px;
        background:#fff; border:1px solid var(--em-primary-100); font-size:.8rem;
        }
        .em-splitbar{ height:10px; background:#edf1f3; border-radius:6px; overflow:hidden; }
        .em-splitbar > div{ height:100%; display:inline-block; }

      `}</style>
      {children}
    </>
  );
};

/*****************
 * TOASTS
 *****************/
const ToastCtx = React.createContext(null);
const useToast = () => React.useContext(ToastCtx);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = React.useState([]);

  const push = React.useCallback((variant, title, msg) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, variant, title, msg }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 4000);
  }, []);

  const api = {
    success: (title, msg) => push("success", title, msg),
    error: (title, msg) => push("danger", title, msg),
    info: (title, msg) => push("info", title, msg),
    warn: (title, msg) => push("warning", title, msg),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast show border-0 shadow-sm text-white mb-2 bg-${t.variant}`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="toast-header border-0">
              <strong className="me-auto">{t.title}</strong>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}
              />
            </div>
            {t.msg && <div className="toast-body">{t.msg}</div>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
};

/*****************
 * CONFIRM MODAL
 *****************/
const ConfirmModal = ({
  show,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}) => {
  if (!show) return null;
  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.4)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body">
            <p className="mb-0">{message}</p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>
              {cancelText}
            </button>
            <button className="btn btn-primary" onClick={onConfirm}>
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ALLOWED_ROLES = ["admin", "sales"];

const LOGO_URL =
  "https://www.energymatters.com.au/wp-content/uploads/2024/11/EM-20-Years-1024x226.png.webp";

// API Service
const API_BASE_URL =
  "https://sai39n8bjf.execute-api.ap-southeast-2.amazonaws.com/beta/leads";

const apiService = {
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/login1`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });
    const data = await response.json();

    if (data.accounts && data.accounts.length > 0) {
      return { multiAccount: true, accounts: data.accounts };
    } else {
      return { multiAccount: false, user: data };
    }
  },

  getAllLeads: async (userId) => {
    const response = await fetch(`${API_BASE_URL}/all?id=${userId}`);
    return response.json();
  },

  getPartnerLeads: async (partnerId) => {
    const response = await fetch(`${API_BASE_URL}/partner?id=${partnerId}`);
    return response.json();
  },

  getPartners: async (id, type) => {
    const response = await fetch(
      `${API_BASE_URL}/getdd_v2?id=${id}&type=${encodeURIComponent(type)}`
    );
    return response.json();
  },

  getAssignments: async (id) => {
    const response = await fetch(
      `${API_BASE_URL}/getlistofassignment?id=${id}`
    );
    return response.json();
  },

  rejectLead: async (quoteid, partnerid, reason) => {
    const response = await fetch(
      `${API_BASE_URL}/rejectlead/?quoteid=${quoteid}&partnerid=${partnerid}&reason=${encodeURIComponent(
        reason
      )}`,
      {
        method: "POST",
      }
    );
    return response.json();
  },

  makeNonLegit: async (id) => {
    const response = await fetch(`${API_BASE_URL}/legitimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    return response.json();
  },

  deleteLead: async (id) => {
    const response = await fetch(`${API_BASE_URL}/deletelead/?id=${id}`, {
      method: "DELETE",
    });
    return response.json();
  },

  getPartnersList: async (userContext) => {
    const response = await fetch(`${API_BASE_URL}/customer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: userContext,
      }),
    });
    return response.json(); // Return partners as-is, no grouping
  },

  getPartnerById: async (partnerId) => {
    const response = await fetch(
      `${API_BASE_URL}/customerbyid?id=${partnerId}`
    );
    return response.json();
  },

  switchAccount: async (accountId, email) => {
    const response = await fetch(`${API_BASE_URL}/switch-account`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accountId, email }),
    });
    return response.json();
  },

  getPartnersByEmail: async (email, userContext) => {
    const response = await fetch(`${API_BASE_URL}/partnersbyemail`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        user: userContext,
      }),
    });
    return response.json();
  },

  updateMultiplePartners: async (partnersData, userContext) => {
    const response = await fetch(`${API_BASE_URL}/updatemultiplepartners`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        partners: partnersData,
        user: userContext,
      }),
    });
    return response.json();
  },

  updateCustomer: async (customerData, userContext) => {
    const response = await fetch(`${API_BASE_URL}/updatecustomer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...customerData, // send each field as root field, not nested
        user: userContext,
      }),
    });
    if (!response.ok) throw new Error("Failed to update customer");
    return response.json();
  },

  updateCustomerAdmin: async (adminData, userContext) => {
    const response = await fetch(`${API_BASE_URL}/updatecustomeradmin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...adminData, // send each field as a root-level property
        user: userContext,
      }),
    });
    if (!response.ok) throw new Error("Failed to update customer as admin");
    return response.json();
  },
};

apiService.getCustomerUnassignment = async (partnerId) => {
  const response = await fetch(
    `${API_BASE_URL}/getcustomerunassignment?id=${partnerId}`
  );
  return response.json();
};

apiService.getCustomerAssignment = async (partnerId) => {
  const res = await fetch(
    `${API_BASE_URL}/getcustomerassignment?id=${partnerId}`
  );
  return res.json();
};

apiService.setPrepaid = async ({ partnerid, prepaid }) => {
  const res = await fetch(`${API_BASE_URL}/setprepaid`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ partnerid, prepaid }),
  });
  if (!res.ok) throw new Error("Failed to update prepaid status");
  return res.json();
};

// ✅ Approve Partner (admin only)
apiService.approvePartner = async (partnerId) => {
  const url = `https://sai39n8bjf.execute-api.ap-southeast-2.amazonaws.com/beta/leads/approvecustomer?id=${encodeURIComponent(
    partnerId
  )}`;

  const res = await fetch(url, { method: "GET" });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Approval failed: ${res.status} ${text}`);
  }

  return res.json().catch(() => ({}));
};

// 🔐 Update user password (POST method)
apiService.updatePassword = async ({ id, password }) => {
  const res = await fetch(
    "https://sai39n8bjf.execute-api.ap-southeast-2.amazonaws.com/beta/leads/updatepassword",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, password }),
    }
  );

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Password update failed: ${res.status} ${text}`);
  }

  return res.json().catch(() => ({}));
};

// App.js (where apiService lives)
apiService.getLeadOptions = async () => {
  // ⬇️ change the path to whatever your backend exposes
  const res = await fetch(`${API_BASE_URL}/lead-options`, {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to fetch lead options: ${res.status}`);
  const data = await res.json();
  // Expecting: [{ value: "Residential+Solar", label: "Residential Solar" }, ...]
  return Array.isArray(data) ? data : [];
};

// --- API helper
apiService.sendPodiumMessage = async ({ to, message }) => {
  const res = await fetch(
    "https://sai39n8bjf.execute-api.ap-southeast-2.amazonaws.com/beta/podium/message",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to, message }),
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`SMS failed: ${res.status} ${res.statusText} ${text}`);
  }
  return res.json().catch(() => ({}));
};

// --- helpers
const getFirstName = (full) =>
  String(full || "")
    .trim()
    .split(/\s+/)[0] || "";
const normalizeAuMobile = (input) => {
  if (!input) return input;
  const d = String(input).replace(/[^\d+0-9]/g, "");
  if (d.startsWith("+")) return d;
  if (/^04\d{8}$/.test(d)) return "+61" + d.slice(1);
  if (/^4\d{8}$/.test(d)) return "+61" + d;
  if (/^614\d{8}$/.test(d)) return "+" + d;
  return d;
};

// --- minimal modal used by both places
const SmsModal = ({
  open,
  onClose,
  to,
  initial,
  onSend,
  title = "Send SMS",
}) => {
  const [msg, setMsg] = React.useState(initial || "");
  React.useEffect(() => setMsg(initial || ""), [initial, open]);
  if (!open) return null;
  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,.35)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label">To</label>
              <input className="form-control" value={to || ""} readOnly />
            </div>
            <div>
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows={4}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Type your message…"
              />
              <div className="form-text">You can edit before sending.</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => onSend(msg)}
              disabled={!msg?.trim()}
            >
              Send SMS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- helper for POST JSON (add once inside apiService scope) ---
const postJSON = async (url, body) => {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
  }
  return res.json();
};

apiService.getRejectsOverview = (from, to, cohort = "assigned") =>
  postJSON(`${API_BASE_URL}/stats-overview`, {
    fromts: from,
    tots: to,
    cohort,
  });

apiService.getRejectsByPartner = (from, to, limit = 10, cohort = "assigned") =>
  postJSON(`${API_BASE_URL}/stats-rejectbypartner`, {
    fromts: from,
    tots: to,
    limit,
    cohort,
  });

apiService.getRejectsTrendWeekly = (from, to, cohort = "assigned") =>
  postJSON(`${API_BASE_URL}/stats-trendweekly`, {
    fromts: from,
    tots: to,
    cohort,
  });

apiService.getRejectsByGeo = (from, to, group = "state", cohort = "assigned") =>
  postJSON(`${API_BASE_URL}/stats-rejectbybio`, {
    fromts: from,
    tots: to,
    group,
    cohort,
  });

// NEW
apiService.getPostcodeMonthly = (year, state = "ALL") =>
  postJSON(`${API_BASE_URL}/stats-postcode-monthly`, { year, state });

apiService.getCompanyYear = (year) =>
  postJSON(`${API_BASE_URL}/stats-company-year`, { year });

apiService.getMonthView = (state = "ALL", fromts, tots) =>
  postJSON(`${API_BASE_URL}/stats-monthview`, { state, fromts, tots });

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableAccounts, setAvailableAccounts] = useState([]);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedAccounts = localStorage.getItem("availableAccounts");

    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedAccounts) {
      setAvailableAccounts(JSON.parse(savedAccounts));
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await apiService.login(credentials);

      // Normalize account structure - ADD THIS HELPER FUNCTION
      const normalizeAccount = (account) => ({
        ...account,
        id: account.id || account.partnerid, // Ensure id field exists
      });

      if (response.multiAccount) {
        const normalizedAccounts = response.accounts.map(normalizeAccount);
        console.log("Multi-account login:", normalizedAccounts);
        setAvailableAccounts(normalizedAccounts);
        localStorage.setItem(
          "availableAccounts",
          JSON.stringify(normalizedAccounts)
        );
        return { requiresAccountSelection: true, accounts: normalizedAccounts };
      } else {
        const normalizedUser = normalizeAccount(response.user);
        console.log("Single account login:", normalizedUser);
        setUser(normalizedUser);
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        setAvailableAccounts([normalizedUser]);
        localStorage.setItem(
          "availableAccounts",
          JSON.stringify([normalizedUser])
        );
        return { requiresAccountSelection: false, user: normalizedUser };
      }
    } catch (error) {
      throw new Error("Login failed");
    }
  };

  const selectAccount = (account) => {
    console.log("Selecting account:", account);
    // Normalize the account when selecting
    const normalizedAccount = {
      ...account,
      id: account.id || account.partnerid,
    };
    setUser(normalizedAccount);
    localStorage.setItem("user", JSON.stringify(normalizedAccount));
  };

  const switchAccount = (partnerId) => {
    console.log("switchAccount called with partnerid:", partnerId);
    console.log("Available accounts:", availableAccounts);

    const targetAccount = availableAccounts.find(
      (acc) => String(acc.partnerid) === String(partnerId)
    );
    console.log("Target account found:", targetAccount);

    if (targetAccount) {
      // Normalize the account when switching
      const normalizedAccount = {
        ...targetAccount,
        id: targetAccount.id || targetAccount.partnerid,
      };
      setUser(normalizedAccount);
      localStorage.setItem("user", JSON.stringify(normalizedAccount));
      console.log("Account switched successfully to:", normalizedAccount);
      return normalizedAccount;
    } else {
      console.error("Account not found in available accounts");
      throw new Error("Account not found");
    }
  };

  const logout = () => {
    setUser(null);
    setAvailableAccounts([]);
    localStorage.removeItem("user");
    localStorage.removeItem("availableAccounts");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        availableAccounts,
        login,
        selectAccount,
        switchAccount,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Login Component
// --- Login (drop-in replacement) ---
const Login = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const { login, selectAccount } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(credentials);
      if (result.requiresAccountSelection) {
        setAccounts(result.accounts);
        setShowAccountSelection(true);
      }
    } catch {
      setError("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (account) => {
    selectAccount(account);
    setShowAccountSelection(false);
  };

  // Account selection step
  if (showAccountSelection) {
    return (
      <div
        className="d-flex align-items-center justify-content-center min-vh-100"
        style={{ background: "var(--em-light)" }}
      >
        <div className="card shadow w-100" style={{ maxWidth: 420 }}>
          <div className="card-body">
            <div className="text-center mb-4">
              <img src={LOGO_URL} alt="Logo" style={{ maxWidth: 140 }} />
              <h2 className="h4 mt-3 mb-1">Select Account</h2>
              <div className="text-muted">Choose which account to access</div>
            </div>

            <div className="list-group mb-3">
              {accounts.map((acc) => (
                <button
                  key={acc.partnerid}
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                  onClick={() => handleAccountSelect(acc)}
                >
                  <div
                    className="rounded-circle text-white d-flex align-items-center justify-content-center"
                    style={{
                      width: 32,
                      height: 32,
                      background: "var(--em-primary)",
                    }}
                  >
                    {String(acc.firstName || "P")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div className="text-start">
                    <div className="fw-semibold">{acc.firstName}</div>
                    <div className="small text-muted">
                      {acc.type === "admin" ? "Administrator" : "Partner"} • ID:{" "}
                      {acc.partnerid}
                    </div>
                    <div className="small text-secondary">{acc.username}</div>
                  </div>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="btn w-100"
              style={{
                borderColor: "var(--em-primary)",
                color: "var(--em-primary)",
              }}
              onClick={() => setShowAccountSelection(false)}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Standard login
  return (
    <div
      className="d-flex align-items-center justify-content-center min-vh-100"
      style={{ background: "var(--em-light)" }}
    >
      <div className="card shadow w-100" style={{ maxWidth: 420 }}>
        <div className="card-body">
          <div className="mb-4 text-center">
            <img
              src={LOGO_URL}
              alt="Energy Matters"
              style={{ maxWidth: 140, marginBottom: 16 }}
            />
            <div className="text-muted mb-3">Sign in to your account</div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                type="email"
                className="form-control"
                value={credentials.username}
                onChange={(e) =>
                  setCredentials({ ...credentials, username: e.target.value })
                }
                placeholder="Enter email"
                required
                autoFocus
              />
              <div className="form-text">Use your email address.</div>
            </div>

            <div className="mb-3">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                placeholder="Enter password"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="alert alert-danger text-center py-2">{error}</div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>

            <div className="d-flex justify-content-between mt-3 small">
              <span className="text-muted">Need help? Contact admin</span>
              <a
                href="#"
                style={{ color: "var(--em-primary)" }}
                onClick={(e) => e.preventDefault()}
              >
                Forgot password
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Header Component
// --- Header Component (drop-in for App.js) ---
// ===== Header (with account switch) =====
const Header = () => {
  const { user, availableAccounts, switchAccount, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  // close dropdown on outside click
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const otherAccounts = (availableAccounts || []).filter(
    (a) => String(a.partnerid ?? a.id) !== String(user?.partnerid ?? user?.id)
  );

  const handleSwitch = (acc) => {
    try {
      switchAccount(acc.partnerid ?? acc.id);
      setOpen(false);
    } catch (err) {
      alert("Failed to switch account: " + (err?.message || "Unknown error"));
    }
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom shadow-sm px-4 py-2">
      <div className="container-fluid">
        {/* Logo + Title */}
        <div className="d-flex align-items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Energy Matters"
            style={{ height: 32, width: "auto" }}
          />
          <span className="navbar-brand mb-0 h5">Leads Management Portal</span>
        </div>

        {/* Right side */}
        <div className="d-flex align-items-center gap-3 ms-auto">
          {/* Current user pill */}
          <div className="d-flex align-items-center gap-2">
            <User size={18} />
            <span className="fw-semibold">{user?.firstName}</span>
            <span className="badge bg-primary text-uppercase">
              {user?.type}
            </span>
            <span className="badge bg-secondary">
              ID: {user?.partnerid ?? user?.id}
            </span>
          </div>

          {/* Switch account (only if >1) */}
          {otherAccounts.length > 0 && (
            <div className="position-relative" ref={menuRef}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                title="Switch account"
              >
                Switch <ChevronDown size={14} />
              </button>

              {open && (
                <div
                  className="dropdown-menu show shadow-sm"
                  style={{ right: 0, left: "auto", minWidth: 260 }}
                  role="menu"
                >
                  <div className="dropdown-header small text-muted">
                    Switch to:
                  </div>
                  {otherAccounts.map((acc) => (
                    <button
                      key={acc.partnerid ?? acc.id}
                      className="dropdown-item text-wrap"
                      onClick={() => handleSwitch(acc)}
                      role="menuitem"
                    >
                      <div className="fw-semibold">
                        {acc.firstName || acc.companyname || "Account"}
                      </div>
                      <div className="small text-muted">
                        {(acc.type === "admin" ? "Administrator" : "Partner") +
                          " • ID: " +
                          (acc.partnerid ?? acc.id)}
                      </div>
                      <div className="small text-secondary">{acc.username}</div>
                    </button>
                  ))}
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item text-muted"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <button
            type="button"
            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
            onClick={logout}
            title="Sign out"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

// --- Debounce helper (paste once near the top of App.js) ---
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// Sidebar Component (drop-in replacement)
const Sidebar = ({ activeTab, setActiveTab }) => {
  const { user } = useAuth();

  const menuItems = [
    {
      id: "quotes",
      label: user?.type === "partner" ? "My Leads" : "Quotes",
      icon: FileText,
    },
    {
      id: "partners",
      label: user?.type === "partner" ? "My Profile" : "Partners",
      icon: Users,
    },
    { id: "stats", label: "Statistics", icon: BarChart3, roles: ALLOWED_ROLES },
    {
      id: "password",
      label: "Update Password",
      icon: FileText,
    },
  ];

  const filteredItems = menuItems.filter(
    (item) => !item.roles || item.roles.includes(user?.type)
  );

  return (
    <aside
      className="d-flex flex-column text-white p-3 vh-100"
      style={{
        width: 240,
        minWidth: 200,
        background: "var(--em-dark, #1B1F24)",
      }}
    >
      <div className="d-flex align-items-center mb-4 gap-2">
        <Home size={22} />
        <span className="fs-5 fw-semibold">Dashboard</span>
      </div>

      <nav>
        <ul className="nav nav-pills flex-column gap-2">
          {filteredItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <li className="nav-item" key={item.id}>
                <button
                  type="button"
                  className={`nav-link text-start d-flex align-items-center justify-content-between w-100 ${
                    isActive ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(item.id)}
                  style={{
                    border: "none",
                    background: isActive
                      ? "var(--em-primary, #F36E21)"
                      : "transparent",
                    color: "#fff",
                  }}
                >
                  <span className="d-flex align-items-center gap-2">
                    <Icon size={18} />
                    {item.label}
                  </span>
                  {isActive && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        background: "rgba(255,255,255,.2)",
                        color: "#fff",
                      }}
                    >
                      •
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-auto small text-white-50">
        <div className="opacity-75">Signed in as</div>
        <div className="fw-semibold">{user?.firstName}</div>
      </div>
    </aside>
  );
};

// Quote Details Modal
// --- Quote Details Modal (drop-in replacement) ---
const QuoteDetailsModal = ({ quote, onClose, onSave }) => {
  const { user } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState({
    firstname: quote?.firstname || "",
    lastname: quote?.lastname || "",
    email: quote?.email || "",
    phone: quote?.phone || "",
    state: quote?.state || "",
    addr1: quote?.addr1 || "",
    suburb: quote?.suburb || "",
    postcode: quote?.postcode || "",
    quotetype: quote?.quotetype || "",
    memo: quote?.memo || "",
  });

  const [partners, setPartners] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDropdown, setShowRejectDropdown] = useState(false);
  const [otherRejectReason, setOtherRejectReason] = useState("");
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const rejectRef = React.useRef(null);
  useOnClickOutside(rejectRef, () => setShowRejectDropdown(false));

  // confirm dialog for assignment
  const [confirmAssign, setConfirmAssign] = useState({
    show: false,
    nextId: null,
  });

  const rejectReasons = [
    "Not interested",
    "Already installed",
    "Budget constraints",
    "Wrong location",
    "Duplicate lead",
    "Other",
  ];

  const myAssignment =
    user?.type === "partner"
      ? assignments.find((a) => String(a.partner) === String(user.partnerid))
      : null;

  const isPendingAdminApproval =
    user?.type === "partner" &&
    myAssignment &&
    myAssignment.hhold === 1 &&
    myAssignment.rejected === 0;

  const canShowRejectDropdown =
    ALLOWED_ROLES.includes(user?.type) ||
    (user?.type === "partner" && !isPendingAdminApproval);

  useEffect(() => {
    if (!quote) return;
    loadPartners();
    loadAssignments();
    // eslint-disable-next-line
  }, [quote?.id]);

  const loadPartners = async () => {
    try {
      const data = await apiService.getPartners(
        quote.postcode,
        quote.quotetype || ""
      );
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadAssignments = async () => {
    setLoadingAssignments(true);
    try {
      const data = await apiService.getAssignments(quote.id);
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleRejectLead = async () => {
    const partnerIdToUse = ALLOWED_ROLES.includes(user?.type)
      ? selectedPartner
      : user?.partnerid;
    const reasonToSend =
      rejectReason === "Other" ? otherRejectReason : rejectReason;

    if (
      !reasonToSend ||
      (!partnerIdToUse && ALLOWED_ROLES.includes(user?.type))
    ) {
      toast.warn("Missing info", "Select a partner and reason first.");
      return;
    }

    try {
      await apiService.rejectLead(quote.id, partnerIdToUse, reasonToSend);
      await loadAssignments();
      toast.success("Lead rejected", "Assignment record updated.");
      onSave && onSave(formData);
      onClose && onClose();
    } catch (err) {
      toast.error("Reject failed", "Please try again.");
    }
  };

  const handleMakeNonLegit = async () => {
    try {
      await apiService.makeNonLegit(quote.id);
      toast.warn("Marked Non-Legit", "Lead flagged as non-legitimate.");
      onSave && onSave(formData);
      onClose && onClose();
    } catch (e) {
      toast.error("Update failed", "Could not mark as non-legit.");
    }
  };

  if (!quote) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Lead Details</h5>
            <button
              type="button"
              className="btn btn-link p-0"
              aria-label="Close"
              onClick={onClose}
            >
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            <form>
              {/* Customer Details */}
              <fieldset className="em-section shadow-sm">
                <legend className="em-legend">Customer Details</legend>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.firstname}
                      onChange={(e) =>
                        setFormData({ ...formData, firstname: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.lastname}
                      onChange={(e) =>
                        setFormData({ ...formData, lastname: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.state}
                      onChange={(e) =>
                        setFormData({ ...formData, state: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Postcode</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.postcode}
                      onChange={(e) =>
                        setFormData({ ...formData, postcode: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Suburb</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.suburb}
                      onChange={(e) =>
                        setFormData({ ...formData, suburb: e.target.value })
                      }
                    />
                  </div>
                </div>
              </fieldset>

              {/* Lead Details */}
              <fieldset className="em-section shadow-sm">
                <legend className="em-legend">Lead Details</legend>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.addr1}
                      onChange={(e) =>
                        setFormData({ ...formData, addr1: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Leadtype Selected</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.quotetype}
                      onChange={(e) =>
                        setFormData({ ...formData, quotetype: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Comments</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={formData.memo}
                      onChange={(e) =>
                        setFormData({ ...formData, memo: e.target.value })
                      }
                    />
                  </div>
                </div>
              </fieldset>

              {/* Partner Assignment / Actions */}
              <fieldset className="em-section shadow-sm">
                <legend className="em-legend">
                  {ALLOWED_ROLES.includes(user?.type)
                    ? "Partner Assignment"
                    : "Lead Actions"}
                </legend>{" "}
                {ALLOWED_ROLES.includes(user?.type) && (
                  <div className="mb-3">
                    <label className="form-label">Select Partner</label>
                    <select
                      className="form-select"
                      value={selectedPartner ?? ""}
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return setSelectedPartner("");
                        setConfirmAssign({ show: true, nextId: id });
                      }}
                    >
                      <option value="">Choose partner...</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.companyname}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Reject */}
                <div className="mb-3">
                  <label className="form-label">Reject Reason</label>

                  {isPendingAdminApproval ? (
                    <div className="alert alert-warning fw-bold mb-0">
                      Your rejection request has been sent and is pending admin
                      approval.
                    </div>
                  ) : canShowRejectDropdown ? (
                    <div ref={rejectRef} className="position-relative">
                      {/* Use form-control (not form-select) to avoid native arrow */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="form-control d-flex align-items-center"
                        onClick={() => setShowRejectDropdown((v) => !v)}
                        onKeyDown={(e) =>
                          (e.key === "Enter" || e.key === " ") &&
                          setShowRejectDropdown((v) => !v)
                        }
                        aria-haspopup="listbox"
                        aria-expanded={showRejectDropdown}
                      >
                        <span className="me-auto">
                          {rejectReason || "Select reason..."}
                        </span>
                        <ChevronDown size={18} />
                      </div>

                      {showRejectDropdown && (
                        <div
                          className="shadow-sm border rounded position-absolute w-100 mt-1"
                          style={{
                            top: "100%",
                            left: 0,
                            zIndex: 1080,
                            background: "#fff",
                          }}
                          role="listbox"
                        >
                          {rejectReasons.map((reason) => (
                            <button
                              key={reason}
                              type="button"
                              className="dropdown-item"
                              onClick={() => {
                                setRejectReason(reason);
                                setShowRejectDropdown(false);
                              }}
                            >
                              {reason}
                            </button>
                          ))}
                        </div>
                      )}

                      {rejectReason === "Other" && (
                        <input
                          type="text"
                          className="form-control mt-2"
                          value={otherRejectReason}
                          onChange={(e) => setOtherRejectReason(e.target.value)}
                          placeholder="Please specify the reason"
                        />
                      )}
                    </div>
                  ) : null}
                </div>
                {/* Assignment history (admin/sales only) */}
                {ALLOWED_ROLES.includes(user?.type) && (
                  <div className="mt-4">
                    <h6 className="fw-semibold mb-2">Assignment History</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Status</th>
                            <th>Date Assigned</th>
                            <th>Partner</th>
                            <th>Reason</th>
                            <th>Last updated on</th>
                            <th>Assigned By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignments.map((a, idx) => (
                            <tr key={idx}>
                              <td>
                                <span
                                  className={`badge fw-bold px-3 py-2 bg-${
                                    a.rejected
                                      ? "danger"
                                      : a.hold
                                      ? "warning"
                                      : "success"
                                  } bg-opacity-25 text-${
                                    a.rejected
                                      ? "danger"
                                      : a.hold
                                      ? "warning"
                                      : "success"
                                  }`}
                                  style={{ fontSize: "0.95em" }}
                                >
                                  {a.rejected
                                    ? "Rejected"
                                    : a.hold
                                    ? "Pending Admin Approval"
                                    : "Accepted"}
                                </span>
                              </td>
                              <td>
                                {a.date_assigned
                                  ? new Date(
                                      a.date_assigned
                                    ).toLocaleDateString()
                                  : ""}
                              </td>
                              <td>{a.companyname}</td>
                              <td>{a.reason || ""}</td>
                              <td>
                                {a.date
                                  ? new Date(a.date).toLocaleDateString()
                                  : ""}
                              </td>
                              <td>
                                {a.assigned_by
                                  ? a.assigned_by
                                  : "Auto assignment"}
                              </td>
                            </tr>
                          ))}
                          {loadingAssignments && (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center text-muted py-3"
                              >
                                Loading assignments…
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </fieldset>
            </form>
          </div>

          <div className="modal-footer d-flex justify-content-between flex-wrap gap-2">
            <div>
              <button
                type="button"
                className="btn btn-danger me-2"
                onClick={handleRejectLead}
                disabled={
                  (ALLOWED_ROLES.includes(user?.type)
                    ? !selectedPartner || !rejectReason
                    : !rejectReason) || isPendingAdminApproval
                }
              >
                Reject Lead
              </button>
              {ALLOWED_ROLES.includes(user?.type) && (
                <button
                  type="button"
                  className="btn btn-warning text-white"
                  onClick={handleMakeNonLegit}
                >
                  Make Non-Legit
                </button>
              )}
            </div>
            <div>
              <CustomerSmsButton
                lead={quote}
                partnerName={
                  (
                    partners.find(
                      (p) => String(p.id) === String(selectedPartner)
                    ) || {}
                  ).companyname
                }
              />

              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => {
                  onSave && onSave(formData);
                  onClose && onClose();
                }}
              >
                <Save size={18} />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm partner assignment */}
      <ConfirmModal
        show={confirmAssign.show}
        title="Assign Lead"
        message="Do you want to assign this lead to the selected partner?"
        confirmText="Assign"
        onCancel={() => setConfirmAssign({ show: false, nextId: null })}
        onConfirm={async () => {
          try {
            const resp = await fetch(`${API_BASE_URL}/updatepartner`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user: user.username,
                partnerid: confirmAssign.nextId,
                id: quote.id,
              }),
            });
            const data = await resp.json();
            if (data?.response?.body?.success) {
              setSelectedPartner(confirmAssign.nextId);
              await loadAssignments();
              toast.success("Assigned", "Partner assigned successfully.");
            } else {
              toast.error("Failed", "Could not assign partner.");
            }
          } catch (e) {
            toast.error("Error", "Assignment failed.");
          } finally {
            setConfirmAssign({ show: false, nextId: null });
          }
        }}
      />
    </div>
  );
};

// Quotes List Component (drop-in replacement)
const QuotesList = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [filteredQuotes, setFilteredQuotes] = useState([]);

  // simple pager
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { user } = useAuth();
  const isPrivileged = ["admin", "sales"].includes(user?.type);

  // inside QuotesList, after your useState hooks
  const isAdmin = user?.type === "admin";

  // rows currently visible in the table (fallback to all quotes)
  const visibleRows = React.useMemo(
    () => (filteredQuotes?.length ? filteredQuotes : quotes),
    [filteredQuotes, quotes]
  );

  // CSV escape
  const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportExcel = () => {
    const rows = visibleRows || [];
    if (!rows.length) {
      alert("No rows to export.");
      return;
    }

    const cols = isAdmin
      ? [
          { key: "firstname", header: "First Name" },
          { key: "lastname", header: "Last Name" },
          { key: "phone", header: "Phone" },
          { key: "email", header: "Email" },
          { key: "addr1", header: "Address" },
          { key: "suburb", header: "Suburb" },
          { key: "state", header: "State" },
          { key: "postcode", header: "Postcode" },
          { key: "date_created", header: "Date" },
          { key: "quotetype", header: "Lead Types" },
          { key: "source", header: "Source" },
          { key: "legitimate", header: "Legitimacy" },
          { key: "processed", header: "Assigned Count" },
          { key: "id", header: "Lead ID" },
        ]
      : [
          { key: "firstname", header: "First Name" },
          { key: "lastname", header: "Last Name" },
          { key: "phone", header: "Phone" },
          { key: "email", header: "Email" },
          { key: "suburb", header: "Suburb" },
          { key: "state", header: "State" },
          { key: "postcode", header: "Postcode" },
          { key: "date_created", header: "Date" },
          { key: "quotetype", header: "Lead Types" },
          { key: "source", header: "Source" },
          { key: "id", header: "Lead ID" },
        ];

    const header = cols.map((c) => csvCell(c.header)).join(",");

    const formatVal = (key, q) => {
      if (key === "date_created") {
        return q.date_created
          ? new Date(q.date_created).toLocaleDateString("en-AU")
          : "";
      }
      if (key === "legitimate")
        return q.legitimate === 0 ? "Legit" : "Non-Legit";
      return q[key] ?? "";
    };

    const data = rows
      .map((q) => cols.map((c) => csvCell(formatVal(c.key, q))).join(","))
      .join("\n");

    const csv = `${header}\n${data}`;
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (user?.id || user?.partnerid) loadQuotes();
    // eslint-disable-next-line
  }, [user?.id, user?.type]);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      let quotesData;
      if (user?.type === "partner") {
        quotesData = await apiService.getPartnerLeads(user.partnerid);
      } else {
        quotesData = await apiService.getAllLeads(user.id);
      }
      setQuotes(Array.isArray(quotesData) ? quotesData : []);
    } catch (error) {
      console.error("Failed to load quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = React.useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return quotes;
    return quotes.filter(
      (q) =>
        (q.firstname || "").toLowerCase().includes(term) ||
        (q.lastname || "").toLowerCase().includes(term) ||
        (q.email || "").toLowerCase().includes(term) ||
        (q.quotetype || "").toLowerCase().includes(term) ||
        (q.phone || "").includes(term)
    );
  }, [quotes, debouncedSearch]);

  // reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await apiService.deleteLead(quoteId);
      await loadQuotes();
    } catch (error) {
      console.error("Failed to delete quote:", error);
    }
  };

  // pagination slice
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  return (
    <div className="container-fluid px-0">
      {/* Heading & Export */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 className="h4 mb-0 fw-bold">
          {user?.type === "partner" ? "My Leads" : "Quotes Management"}
        </h2>
        <div className="d-flex align-items-center gap-3">
          <span className="text-muted small">
            {user?.type === "partner"
              ? `Partner: ${user?.firstName} (ID: ${user?.partnerid})`
              : `Admin: ${user?.firstName} (ID: ${user?.id})`}
          </span>
          {isAdmin && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleExportExcel}
              disabled={!visibleRows?.length}
              title={
                visibleRows?.length
                  ? "Export visible leads to Excel"
                  : "Nothing to export"
              }
              type="button"
            >
              Export as Excel
            </button>
          )}{" "}
        </div>
      </div>

      {/* Search, Filter, Pager */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div
          className="position-relative"
          style={{ minWidth: 260, maxWidth: 420 }}
        >
          <Search
            className="position-absolute"
            style={{ left: 12, top: 12, color: "#bbb" }}
            size={16}
          />
          <input
            type="text"
            className="form-control ps-5"
            placeholder={
              user?.type === "partner"
                ? "Search my leads..."
                : "Search quotes..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          type="button"
          // (hook up later when you add filters)
          onClick={() => alert("Filters coming soon")}
        >
          <Filter size={16} />
          <span>Filter</span>
        </button>

        <div className="ms-auto d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span className="small">
            Page {page} / {totalPages}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <span className="small text-muted">
            Showing {pageItems.length} of {filtered.length} (total:{" "}
            {quotes.length})
          </span>
        </div>

        <div className="table-responsive" style={{ maxHeight: "65vh" }}>
          <table className="table table-hover mb-0 align-middle">
            <thead
              className="table-light"
              style={{ position: "sticky", top: 0, zIndex: 1 }}
            >
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Date</th>
                <th>Type</th>
                <th>Source</th>
                {["admin", "sales"].includes(user?.type) && (
                  <>
                    <th>Status</th>
                    <th>Assigned count</th>
                  </>
                )}
                <th>Actions</th>
              </tr>
            </thead>

            {/* Loading skeleton rows */}
            {loading ? (
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="placeholder-glow">
                    {Array.from({
                      length: ["admin", "sales"].includes(user?.type) ? 9 : 7,
                    }).map((__, j) => (
                      <td key={j}>
                        <span className="placeholder col-8" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                {pageItems.map((quote) => (
                  <tr key={quote.id}>
                    <td className="fw-semibold">
                      {quote.firstname} {quote.lastname}
                    </td>
                    <td>
                      <div>{quote.phone}</div>
                      <div className="text-muted small">{quote.email}</div>
                    </td>
                    <td>
                      <div>{quote.suburb}</div>
                      <div className="text-muted small">
                        {quote.state} {quote.postcode}
                      </div>
                    </td>
                    <td>
                      {quote.date_created
                        ? new Date(quote.date_created).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="small">
                      {quote.quotetype &&
                        quote.quotetype
                          .split(",")
                          .filter(Boolean)
                          .map((val, idx) => (
                            <div key={idx} className="text-break">
                              {val}
                            </div>
                          ))}
                    </td>
                    <td>{quote.source || "Website"}</td>

                    {["admin", "sales"].includes(user?.type) && (
                      <>
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-1 fw-normal ${
                              quote.legitimate === 0
                                ? "bg-success-subtle text-success"
                                : "bg-warning-subtle text-warning"
                            }`}
                          >
                            {quote.legitimate === 0 ? "Legit" : "Non-Legit"}
                          </span>
                        </td>
                        <td>{quote.processed || 0}</td>
                      </>
                    )}

                    <td>
                      {isPrivileged && (
                        <a
                          href={`https://www.energymatters.com.au/leadportal/updateaddress.php?quoteId=${quote.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title="Update Address"
                        >
                          Update Address
                        </a>
                      )}
                      <button
                        onClick={() => setSelectedQuote(quote)}
                        className="btn btn-link btn-sm text-primary"
                        title="Edit quote"
                      >
                        <Edit3 size={16} />
                      </button>
                      {["admin", "sales"].includes(user?.type) && (
                        <button
                          onClick={() => handleDeleteQuote(quote.id)}
                          className="btn btn-link btn-sm text-danger"
                          title="Delete quote"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {pageItems.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={["admin", "sales"].includes(user?.type) ? 9 : 7}
                      className="text-center text-muted py-5"
                    >
                      <div className="mb-2">No results match your search.</div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear search
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Modal for editing */}
      {selectedQuote && (
        <QuoteDetailsModal
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onSave={loadQuotes}
        />
      )}
    </div>
  );
};

// ===== helpers (keep as you had) =====
const cbgNormalizeToken = (s) =>
  !s
    ? ""
    : String(s)
        .trim()
        .replace(/\u2013|\u2014/g, "-")
        .replace(/\s+/g, "+")
        .replace(/\++/g, "+");

const cbgNormalizeList = (val) => {
  if (Array.isArray(val)) return val.map(cbgNormalizeToken).filter(Boolean);
  if (!val) return [];
  return String(val).split(",").map(cbgNormalizeToken).filter(Boolean);
};

// ===== CheckboxGroup (fetches options from backend) =====
const CheckboxGroup = ({
  name = "leadtypes",
  initialLeadTypes = "",
  onChange,
}) => {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  // fetch once
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await apiService.getLeadOptions();
        if (!cancelled)
          setOptions(
            data.map((o) => ({
              ...o,
              _value: cbgNormalizeToken(o.value),
            }))
          );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError("Failed to load lead types");
          setOptions([]); // graceful fallback
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // selection state
  const [selected, setSelected] = React.useState(() =>
    cbgNormalizeList(initialLeadTypes)
  );

  // keep selected in sync if parent changes initialLeadTypes
  React.useEffect(() => {
    const next = cbgNormalizeList(initialLeadTypes);
    const cur = [...selected].sort().join(",");
    const nxt = [...next].sort().join(",");
    if (cur !== nxt) setSelected(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLeadTypes]);

  const handleChange = (e, _value) => {
    const next = e.target.checked
      ? [...new Set([...selected, _value])]
      : selected.filter((v) => v !== _value);

    setSelected(next);
    onChange?.(name, next.join(","));
  };

  if (loading) {
    return <div className="text-muted">Loading lead types…</div>;
  }
  if (error) {
    return <div className="alert alert-warning py-2">{error}</div>;
  }

  return (
    <div className="row g-2">
      {options.map((opt) => {
        const id = opt._value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        const isChecked = selected.includes(opt._value);
        return (
          <div key={id} className="col-12 col-md-6">
            <div className="form-check">
              <input
                id={id}
                type="checkbox"
                className="form-check-input"
                checked={isChecked}
                onChange={(e) => handleChange(e, opt._value)}
              />
              <label htmlFor={id} className="form-check-label">
                {opt.label}
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- status helpers (single source of truth) ---
const isInactive = (v) => {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s === "y" || s === "1" || s === "true" || s === "inactive";
};
const isActive = (v) => !isInactive(v);
const toYN = (v) => (isInactive(v) ? "Y" : "N");
const sanitizeYN = (v) => (String(v).toUpperCase() === "Y" ? "Y" : "N");

// --- build full payload for admin updates to prevent wiping columns ---
const buildAdminPayload = (p, overrides = {}) => ({
  id: p.id,
  companyname: p.companyname ?? "",
  contactperson: p.contactperson ?? "",
  email: p.email ?? "",
  email2: p.email2 ?? "",
  phone: p.phone ?? "",
  officeaddress: p.officeaddress ?? "",
  leadcoverage: p.leadcoverage ?? "",
  postcodelist: p.postcodelist ?? "",
  postcoderequest: p.postcoderequest ?? "",
  leadtypes: p.leadtypes ?? "",
  comments: p.comments ?? "",
  costperlead: p.costperlead ?? 0,
  credits: p.credits ?? 0,
  inactive: toYN(p.inactive), // normalize to "Y"/"N"
  ...overrides, // e.g. { inactive: "N" }
});

const AssignedLeads = ({ partnerId }) => {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!partnerId) return;
    setLoading(true);
    apiService
      .getCustomerAssignment(partnerId)
      .then((data) => setLeads(Array.isArray(data) ? data : []))
      .catch((e) => console.error("Failed to load assigned leads:", e))
      .finally(() => setLoading(false));
  }, [partnerId]);

  return (
    <fieldset className="em-section shadow-sm">
      <legend className="em-legend">
        Assigned Leads {leads.length ? `(${leads.length})` : ""}
      </legend>

      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="text-muted">No assigned leads.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Postcode</th>
                <th>Company</th>
                <th>Date Assigned</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.firstname} {l.lastname}
                  </td>
                  <td>{l.email}</td>
                  <td>{l.postcode}</td>
                  <td>{l.companyname}</td>
                  <td>
                    {l.date ? new Date(l.date).toLocaleString("en-AU") : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </fieldset>
  );
};

const UnassignedLeads = ({ partnerId }) => {
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!partnerId) return;
    setLoading(true);
    apiService
      .getCustomerUnassignment(partnerId)
      .then((data) => setLeads(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load unassigned leads:", err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  return (
    <fieldset className="em-section shadow-sm">
      <legend className="em-legend">Unassigned / Rejected Leads</legend>
      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="text-muted">No unassigned leads.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Postcode</th>
                <th>Company</th>
                <th>Reason</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, idx) => (
                <tr key={idx}>
                  <td>
                    {l.firstname} {l.lastname}
                  </td>
                  <td>{l.email}</td>
                  <td>{l.postcode}</td>
                  <td>{l.companyname}</td>
                  <td>{l.reason}</td>
                  <td>
                    {l.date ? new Date(l.date).toLocaleString("en-AU") : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </fieldset>
  );
};

const PartnerSmsButton = ({ partner, formData }) => {
  const [open, setOpen] = React.useState(false);
  const firstName = getFirstName(
    formData?.contactperson || partner?.contactperson
  );
  const phone = normalizeAuMobile(formData?.phone || partner?.phone);

  // default template list + a "Custom" fall-back; user can edit in the modal anyway
  const templates = [
    {
      key: "lowcredit",
      label: "Low credit",
      build: () =>
        `Hi ${firstName}, Energy Matters here! You are running low on credit - please reply to arrange a top up`,
    },
    {
      key: "toppedup",
      label: "Topped up",
      build: () =>
        `Hi ${firstName}, Energy Matters here! We have topped up your account with credit`,
    },
    {
      key: "activated",
      label: "Activated",
      build: () =>
        `Hi ${firstName}, Energy Matters here! Your account has been activated`,
    },
    {
      key: "deactivated",
      label: "Deactivated",
      build: () =>
        `Hi ${firstName}, Energy Matters here! Your account has been deactivated - please reply to find out why`,
    },
    {
      key: "custom",
      label: "Custom message",
      build: () => `Hi ${firstName}, Energy Matters here!`,
    },
  ];
  const [tpl, setTpl] = React.useState(templates[0].key);
  const initialMessage = React.useMemo(
    () => (templates.find((t) => t.key === tpl) || templates[0]).build(),
    [tpl, firstName]
  );

  const send = async (message) => {
    await apiService.sendPodiumMessage({ to: phone, message });
    setOpen(false);
    alert("SMS sent");
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 me-auto">
        <select
          className="form-select form-select-sm"
          style={{ width: 220 }}
          value={tpl}
          onChange={(e) => setTpl(e.target.value)}
          title="Choose SMS template"
        >
          {templates.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setOpen(true)}
          disabled={!phone}
          title="Send SMS from Podium"
        >
          Send SMS
        </button>
      </div>

      <SmsModal
        open={open}
        onClose={() => setOpen(false)}
        to={phone}
        initial={initialMessage}
        onSend={send}
        title="Send SMS to partner"
      />
    </>
  );
};

const CustomerSmsButton = ({ lead, partnerName }) => {
  const [open, setOpen] = React.useState(false);
  const firstName = getFirstName(lead?.firstname || lead?.name);
  const phone = normalizeAuMobile(lead?.phone);

  const templates = [
    {
      key: "assigned",
      label: "Assigned message",
      build: () =>
        `Hi ${firstName}, Energy Matters here! We’ve connected you to ${
          partnerName || "our partner"
        } to arrange a quote. Please expect contact soon`,
    },
    {
      key: "custom",
      label: "Custom message",
      build: () => `Hi ${firstName}, Energy Matters here!`,
    },
  ];
  const [tpl, setTpl] = React.useState(templates[0].key);
  const initialMessage = React.useMemo(
    () => (templates.find((t) => t.key === tpl) || templates[0]).build(),
    [tpl, firstName, partnerName]
  );

  const send = async (message) => {
    await apiService.sendPodiumMessage({ to: phone, message });
    setOpen(false);
    alert("SMS sent");
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 me-auto">
        <select
          className="form-select form-select-sm"
          style={{ width: 220 }}
          value={tpl}
          onChange={(e) => setTpl(e.target.value)}
          title="Choose SMS template"
        >
          {templates.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setOpen(true)}
          disabled={!phone}
          title="Send SMS from Podium"
        >
          Send SMS
        </button>
      </div>

      <SmsModal
        open={open}
        onClose={() => setOpen(false)}
        to={phone}
        initial={initialMessage}
        onSend={send}
        title="Send SMS to customer"
      />
    </>
  );
};

// --- Partner Edit Modal (status moved to list; no deep effects) ---
const PartnerEditModal = ({ partner, onClose, onSave }) => {
  const { user } = useAuth();
  const isPrivileged = ALLOWED_ROLES.includes(user?.type);

  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (partner) {
      setFormData({
        companyname: partner.companyname || "",
        contactperson: partner.contactperson || "",
        email: partner.email || "",
        email2: partner.email2 || "",
        phone: partner.phone || "",
        officeaddress: partner.officeaddress || "",
        leadcoverage: partner.leadcoverage || "",
        postcodelist: partner.postcodelist || "",
        postcoderequest: partner.postcoderequest || "",
        leadtypes: partner.leadtypes || "",
        comments: partner.comments || "",
        costperlead: partner.costperlead ?? "",
        credits: partner.credits ?? "",
        limitset: partner.limitset ?? "",
      });
    }
  }, [partner?.id]);

  // 'Y' = postpaid, 'N' = prepaid
  const [billingPrepaid, setBillingPrepaid] = useState("N");
  const [billingLoading, setBillingLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer separate API; fall back to partner.prepaid if already present
        let v = partner?.prepaid;
        if (v == null && typeof apiService?.getPartnerPrepaid === "function") {
          const resp = await apiService.getPartnerPrepaid(partner.id);
          // support either `{ prepaid: 'Y' }` or raw 'Y'
          v = typeof resp === "string" ? resp : resp?.prepaid;
        }
        if (!cancelled) setBillingPrepaid(v === "Y" ? "Y" : "N");
      } catch (e) {
        console.error("Failed to load billing mode:", e);
        if (!cancelled) setBillingPrepaid("N");
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partner?.id, partner?.prepaid]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toList = (s) =>
    Array.from(
      new Set(
        String(s ?? "")
          .replace(/\s+/g, ",")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      )
    );
  const approvedList = toList(formData.postcodelist);
  const requestList = toList(formData.postcoderequest);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isPrivileged) {
        // Admin can update all fields - use updateCustomerAdmin
        const payload = { ...partner, ...formData, id: partner.id };
        await apiService.updateCustomerAdmin(payload, user);
      } else {
        // Partner can only update limited fields - use updateCustomer
        const allowedFields = [
          "contactperson",
          "email",
          "email2",
          "phone",
          "officeaddress",
          "leadcoverage",
          "postcoderequest",
          "leadtypes",
        ];

        const filteredData = Object.fromEntries(
          allowedFields.map((key) => [key, formData[key]])
        );
        filteredData.id = partner.id;

        await apiService.updateCustomer(filteredData, user);
      }

      onSave?.();
      onClose?.();
    } catch (e) {
      console.error("Failed to update partner:", e);
      alert("Failed to update partner");
    } finally {
      setSaving(false);
    }
  };
  if (!partner) return null;

  const handleBillingChange = async (next) => {
    setBillingPrepaid(next);
    // Optional: persist immediately via the dedicated API
    try {
      if (isPrivileged && typeof apiService?.setPrepaid === "function") {
        await apiService.setPrepaid({ partnerid: partner.id, prepaid: next });
      }
    } catch (e) {
      console.error("Failed to update billing mode:", e);
      // revert on failure
      setBillingPrepaid((prev) => (prev === "Y" ? "N" : "Y"));
      alert("Failed to update billing mode");
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              Edit Partner: {partner.companyname}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            {/* Basic Information */}
            <h6 className="fw-semibold mb-3">Basic Information</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.companyname || ""}
                  onChange={(e) => updateField("companyname", e.target.value)}
                  disabled={!isPrivileged}
                />
                {!isPrivileged && (
                  <div className="form-text">
                    Contact support to change company name.
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Contact Person</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.contactperson || ""}
                  onChange={(e) => updateField("contactperson", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Primary Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email || ""}
                  onChange={(e) => updateField("email", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Secondary Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email2 || ""}
                  onChange={(e) => updateField("email2", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.phone || ""}
                  onChange={(e) => updateField("phone", e.target.value)}
                />
              </div>
            </div>

            {/* Business Details */}
            <h6 className="fw-semibold mt-4 mb-3">Business Details</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Office Address</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.officeaddress || ""}
                  onChange={(e) => updateField("officeaddress", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Lead Coverage (free text)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.leadcoverage || ""}
                  onChange={(e) => updateField("leadcoverage", e.target.value)}
                />
              </div>
            </div>

            {/* Lead Types with CheckboxGroup */}
            <div className="mt-4">
              <label className="form-label">Lead Types</label>
              <div className="border rounded p-3">
                <CheckboxGroup
                  name="leadtypes"
                  initialLeadTypes={formData.leadtypes || ""}
                  onChange={updateField}
                />
              </div>
              <div className="form-text mt-2">
                Current selection: {formData.leadtypes || "None selected"}
              </div>
            </div>

            {/* Service Postcodes */}
            <h6 className="fw-semibold mt-4 mb-2">Service Postcodes</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  Approved list (admins only)
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.postcodelist || ""}
                  onChange={(e) => updateField("postcodelist", e.target.value)}
                  placeholder="e.g. 3000,3001,3002"
                  disabled={!isPrivileged}
                />
                <div className="form-text">
                  Parsed {approvedList.length} postcodes.
                </div>
                {!!approvedList.length && (
                  <div className="mt-2">
                    {approvedList.map((p) => (
                      <span
                        key={`ap-${p}`}
                        className="badge rounded-pill bg-light text-dark border me-2 mb-2"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Requested by partner</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.postcoderequest || ""}
                  onChange={(e) =>
                    updateField("postcoderequest", e.target.value)
                  }
                  placeholder="e.g. 3005 3006 3007"
                />
                <div className="form-text">
                  Parsed {requestList.length} postcodes.
                </div>
                {!!requestList.length && (
                  <div className="mt-2">
                    {requestList.map((p) => (
                      <span
                        key={`rq-${p}`}
                        className="badge rounded-pill bg-light text-dark border me-2 mb-2"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Financial */}
            <h6 className="fw-semibold mt-4 mb-3">Financial Information</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Cost Per Lead ($)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.costperlead || ""}
                  onChange={(e) => updateField("costperlead", e.target.value)}
                  disabled={!isPrivileged}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Credits</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.credits || ""}
                  onChange={(e) => updateField("credits", e.target.value)}
                  disabled={!isPrivileged}
                />
              </div>
              {/* Billing mode */}
              <div className="col-md-4">
                <label className="form-label">Billing Mode</label>
                <select
                  className="form-select"
                  value={billingPrepaid}
                  onChange={(e) => handleBillingChange(e.target.value)}
                  disabled={!isPrivileged || billingLoading}
                >
                  <option value="N">Prepaid</option>
                  <option value="Y">Post-paid (Invoice)</option>
                </select>
                <div className="form-text">
                  {billingPrepaid === "Y"
                    ? "Post-paid: invoices issued; credits not required."
                    : "Prepaid: credits are deducted per lead."}
                </div>
              </div>

              {/* Credits (disabled when post-paid) */}
              <div className="col-md-4">
                <label className="form-label">Credits (AUD)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.credits ?? ""}
                  min={0}
                  step="0.01"
                  onChange={(e) => updateField("credits", e.target.value)}
                  disabled={!isPrivileged || billingPrepaid === "Y"}
                />
              </div>

              {/* Daily Lead Limit (unchanged) */}
              <div className="col-md-4">
                <label className="form-label">Daily Lead Limit</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.limitset ?? ""}
                  min={0}
                  step="1"
                  placeholder="e.g. 10"
                  onChange={(e) => updateField("limitset", e.target.value)}
                  disabled={!isPrivileged}
                />
                <div className="form-text">
                  Maximum number of quotes/leads this partner can receive per
                  day.
                </div>
              </div>

              {/* Bank details — only when post-paid */}
              {billingPrepaid === "Y" && (
                <div className="col-12">
                  <label className="form-label">Bank Details</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    readOnly
                    value={`Bank: NAB
Name: All Energy Leads
Bsb: 083802
Acc: 202838135
Ref: your company name`}
                  />
                </div>
              )}
            </div>

            {isPrivileged && (
              <div className="mt-4">
                <label className="form-label">Comments</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.comments || ""}
                  onChange={(e) => updateField("comments", e.target.value)}
                />
              </div>
            )}

            <AssignedLeads partnerId={partner.id} />
            <UnassignedLeads partnerId={partner.id} />
          </div>

          <div className="modal-footer bg-light">
            {/* Send SMS button (inside the edit modal) */}
            <PartnerSmsButton partner={partner} formData={formData} />

            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={16} className="me-1" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PartnersList = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // sort/filter
  const [sortBy, setSortBy] = useState("default"); // "default" | "name" | "nameDesc"
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all"); // all | active | inactive

  const { user } = useAuth();
  const isPrivileged = ALLOWED_ROLES.includes(user?.type);

  useEffect(() => {
    if (user) loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadPartners = async () => {
    setLoading(true);
    try {
      let partnersData;
      if (isPrivileged) {
        const userContext = { ...user, id: user.id || user.partnerid };
        partnersData = await apiService.getPartnersList(userContext);
      } else {
        partnersData = await apiService.getPartnerById(user.partnerid);
        partnersData = Array.isArray(partnersData)
          ? partnersData
          : [partnersData];
      }
      setPartners(partnersData || []);
    } catch (err) {
      console.error("Failed to load partners:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePartner = async (partner) => {
    if (!isPrivileged) return;

    if (
      !window.confirm(
        `Approve partner "${partner.companyname}"?\n\nThis will enable them in the system.`
      )
    ) {
      return;
    }

    try {
      await apiService.approvePartner(partner.id);
      alert(`Partner "${partner.companyname}" approved successfully.`);
      await loadPartners(); // reload list
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to approve partner");
    }
  };

  const handleResetPartnerPassword = async (partner) => {
    if (!isPrivileged) return;

    const label =
      partner.contactperson || partner.companyname || `Partner #${partner.id}`;
    const newPassword = window.prompt(
      `Enter new password for ${label}:\n\n(Leave empty or Cancel to abort)`,
      ""
    );

    if (!newPassword) return;

    try {
      // ⚠️ IMPORTANT:
      // If your login/user id for the partner is not partner.id,
      // replace `partner.id` below with the correct field (e.g. partner.userid).
      await apiService.updatePassword({
        id: partner.id,
        password: newPassword,
      });

      alert(`Password updated for ${label}`);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to reset password");
    }
  };

  // Toggle active/inactive (admin/sales only)
  const toggleInactive = async (partner) => {
    if (!isPrivileged) return;

    const prevYN = toYN(partner.inactive);
    const nextYN = prevYN === "Y" ? "N" : "Y";

    // optimistic UI
    setPartners((prev) =>
      prev.map((p) => (p.id === partner.id ? { ...p, inactive: nextYN } : p))
    );

    try {
      const fresh = partners.find((p) => p.id === partner.id) || partner;
      const payload = buildAdminPayload(fresh, { inactive: nextYN });
      await apiService.updateCustomerAdmin(payload, user);
    } catch (e) {
      // rollback
      setPartners((prev) =>
        prev.map((p) => (p.id === partner.id ? { ...p, inactive: prevYN } : p))
      );
      alert("Failed to update status");
    }
  };

  // Filtered list
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (partners || []).filter((p) => {
      if (status === "active" && !isActive(p.inactive)) return false;
      if (status === "inactive" && isActive(p.inactive)) return false;

      if (term) {
        const haystack = [
          p.companyname,
          p.contactperson,
          p.email,
          p.email2,
          p.phone,
          p.postcodelist,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [partners, search, status]);

  const rows = React.useMemo(() => {
    const norm = (s) =>
      String(s ?? "")
        .trim()
        .replace(/\s+/g, " ");

    const group = (s) => (/^[A-Za-z]/.test(s) ? 0 : 1);

    const list = Array.isArray(visible) ? [...visible] : [];

    list.sort((a, b) => {
      // --- sort by credits ---
      if (sortBy === "creditsHigh") return (b.credits ?? 0) - (a.credits ?? 0);
      if (sortBy === "creditsLow") return (a.credits ?? 0) - (b.credits ?? 0);

      // --- sort by name ---
      const A = norm(a.companyname);
      const B = norm(b.companyname);
      const gA = group(A);
      const gB = group(B);
      if (gA !== gB) return gA - gB;
      const cmp = A.localeCompare(B, "en", {
        sensitivity: "base",
        numeric: true,
      });
      if (sortBy === "nameDesc") return -cmp;
      if (cmp !== 0) return cmp;
      return (a.id ?? 0) - (b.id ?? 0);
    });

    return list;
  }, [visible, sortBy]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 200 }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="text-secondary">Loading partners...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 className="fw-bold fs-4 mb-0">
          {isPrivileged ? "Partners Management" : "My Profile"}
        </h2>

        {isPrivileged && (
          <div className="d-flex align-items-end gap-2">
            <div>
              <label className="form-label small mb-1">Search</label>
              <input
                className="form-control"
                placeholder="Company, contact, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ minWidth: 240 }}
              />
            </div>
            <div>
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="form-label small mb-1">Sort</label>
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Default</option>
                <option value="name">Name (A → Z)</option>
                <option value="nameDesc">Name (Z → A)</option>
                <option value="creditsHigh">Credits (High → Low)</option>
                <option value="creditsLow">Credits (Low → High)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="card shadow-sm mb-3">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Credits</th>
                <th>Status</th>
                <th>Post-paid</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((partner) => {
                const inactive = isInactive(partner.inactive);
                return (
                  <tr key={partner.id}>
                    <td className="fw-semibold">{partner.companyname}</td>
                    <td>{partner.contactperson}</td>
                    <td>{partner.email}</td>
                    <td>{partner.phone}</td>
                    <td>${(partner.credits ?? 0).toLocaleString()}</td>
                    <td>
                      {isPrivileged ? (
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge px-3 py-2 rounded-pill ${
                              !inactive
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {!inactive ? "Active" : "Inactive"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => toggleInactive(partner)}
                            title={
                              inactive ? "Mark as Active" : "Mark as Inactive"
                            }
                          >
                            {inactive ? "Activate" : "Deactivate"}
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`badge px-3 py-2 rounded-pill ${
                            !inactive
                              ? "bg-success-subtle text-success"
                              : "bg-danger-subtle text-danger"
                          }`}
                        >
                          {!inactive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td>
                      {partner.prepaid === "Y" ? (
                        <span className="badge bg-info">Post-paid</span>
                      ) : (
                        <span className="badge bg-secondary">Prepaid</span>
                      )}
                    </td>

                    <td>
                      <td>
                        <td>
                          {isPrivileged && (
                            <button
                              type="button"
                              className="btn btn-sm btn-success me-2"
                              onClick={() => handleApprovePartner(partner)}
                              title="Approve Partner"
                            >
                              Reset password
                            </button>
                          )}

                          <button
                            onClick={() => setSelectedPartner(partner)}
                            className="btn btn-link p-0 text-primary"
                            title="Edit Partner"
                          >
                            <Edit3 size={18} />
                          </button>
                        </td>
                      </td>
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No partners match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedPartner && (
        <PartnerEditModal
          key={selectedPartner.id}
          partner={selectedPartner}
          onClose={() => setSelectedPartner(null)}
          onSave={loadPartners}
        />
      )}
    </div>
  );
};

// Stats Component (Admin only)
// --- Stats: Rejections Focus (drop-in replacement) ---

/*****************
 * REJECTIONS STATS (tab)
 *****************/

// tiny format helpers
const niceDay = (s) => {
  const base = String(s || "").slice(0, 10);
  const dt = new Date(base + "T00:00:00");
  return isNaN(dt.getTime())
    ? base
    : dt.toLocaleDateString("en-AU", { day: "numeric", month: "short" }); // e.g. 4 Aug
};
const fmtNum = (n) => (n == null ? "-" : Number(n).toLocaleString());
const fmtPct = (n, d = 1) =>
  n == null || isNaN(n) ? "-" : `${Number(n).toFixed(d)}%`;
const fmtMin = (m) => (m == null ? "-" : `${Math.round(m)} min`);

// tiny inline charts (no libs)
const LineChart = ({
  values = [],
  height = 80,
  stroke = "var(--em-primary)",
}) => {
  if (!values.length) return <div className="text-muted small">No data</div>;
  const w = Math.max(160, values.length * 28);
  const max = Math.max(100, ...values.map((v) => Number(v) || 0));
  const points = values.map((v, i) => {
    const x = (i / Math.max(1, values.length - 1)) * (w - 8) + 4;
    const y = height - ((Number(v) || 0) / max) * (height - 8) - 4;
    return `${x},${y}`;
  });
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        points={points.join(" ")}
      />
      {points.map((p, i) => {
        const [x, y] = p.split(",").map(Number);
        return <circle key={i} cx={x} cy={y} r="2.8" fill={stroke} />;
      })}
    </svg>
  );
};

const SplitBar = ({ assigned = 0, rejected = 0, holds = 0 }) => {
  const total = Math.max(assigned, 1);
  const r = Math.max(0, Math.min(100, (rejected / total) * 100));
  const h = Math.max(0, Math.min(100, (holds / total) * 100));
  return (
    <div className="em-splitbar">
      <div
        style={{ width: `${r}%`, background: "var(--em-reject)" }}
        title={`Rejected: ${rejected}`}
      />
      <div
        style={{ width: `${h}%`, background: "var(--em-hold)" }}
        title={`Hold: ${holds}`}
      />
    </div>
  );
};

const HBar = ({ label, sublabel, pct, assigned }) => {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-baseline">
        <div className="fw-semibold text-truncate">{label}</div>
        <div className="small text-muted ms-2">
          {fmtPct(p, 1)} • {fmtNum(assigned)} assigned
        </div>
      </div>
      <div className="progress" style={{ height: 8 }}>
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${p}%`, background: "var(--em-primary)" }}
          aria-valuenow={p}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      {sublabel && <div className="small text-muted mt-1">{sublabel}</div>}
    </div>
  );
};

const StatsRejections = () => {
  const toast = useToast();

  // default last 10 weeks
  const today = new Date();
  const tenWeeksAgo = new Date();
  tenWeeksAgo.setDate(today.getDate() - 7 * 10);

  const iso = (d) => d.toISOString().slice(0, 10);
  const [from, setFrom] = React.useState(iso(tenWeeksAgo));
  const [to, setTo] = React.useState(iso(today));
  const [cohort, setCohort] = React.useState("assigned"); // "assigned" | "updated"
  const [geoGroup, setGeoGroup] = React.useState("state"); // "state" | "postcode"

  // data
  const [loading, setLoading] = React.useState(false);
  const [overview, setOverview] = React.useState(null);
  const [byPartner, setByPartner] = React.useState([]);
  const [trend, setTrend] = React.useState([]);
  const [byGeo, setByGeo] = React.useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, p, t, g] = await Promise.all([
        apiService.getRejectsOverview(from, to, cohort),
        apiService.getRejectsByPartner(from, to, 10, cohort),
        apiService.getRejectsTrendWeekly(from, to, cohort),
        apiService.getRejectsByGeo(from, to, geoGroup, cohort),
      ]);
      setOverview(o || null);
      setByPartner(Array.isArray(p) ? p : []);
      // keep only last 10 points (backend might send more)
      const tArr = Array.isArray(t) ? t.slice(-10) : [];
      setTrend(tArr);
      setByGeo(Array.isArray(g) ? g : []);
    } catch (e) {
      console.error(e);
      toast?.error("Failed to load stats", "Check your API responses.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, cohort, geoGroup]);

  return (
    <div className="container-fluid px-0">
      {/* Controls */}
      <div className="d-flex align-items-end flex-wrap gap-3 mb-3">
        <h2 className="h4 fw-bold mb-0">Rejections</h2>
        <div className="ms-auto d-flex align-items-center gap-2">
          <div>
            <label className="form-label small mb-1">From</label>
            <input
              type="date"
              className="form-control"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label small mb-1">To</label>
            <input
              type="date"
              className="form-control"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label small mb-1">Cohort</label>
            <select
              className="form-select"
              value={cohort}
              onChange={(e) => setCohort(e.target.value)}
            >
              <option value="assigned">By assignment time</option>
              <option value="updated">By last update</option>
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Geo</label>
            <select
              className="form-select"
              value={geoGroup}
              onChange={(e) => setGeoGroup(e.target.value)}
            >
              <option value="state">State</option>
              <option value="postcode">Postcode</option>
            </select>
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Assignments</div>
              <div className="fs-4 fw-bold">
                {fmtNum(overview?.assignments)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Rejected</div>
              <div className="fs-4 fw-bold">{fmtNum(overview?.rejected)}</div>
              <div className="small text-muted">
                Range: {from} → {to}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Pending Holds</div>
              <div className="fs-4 fw-bold">
                {fmtNum(overview?.pending_holds)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Acceptance</div>
              <div className="d-flex gap-3 align-items-baseline">
                <div>
                  <div className="small text-muted">Accepted</div>
                  <div className="fw-semibold">
                    {fmtNum(overview?.accepted)}
                  </div>
                </div>
                <div>
                  <div className="small text-muted">Rate</div>
                  <div className="fw-semibold">
                    {fmtPct(overview?.acceptance_rate_pct, 1)}
                  </div>
                </div>
              </div>
              <hr className="my-2" />
              <div className="small text-muted mb-1">Holds age (pending)</div>
              <div className="d-flex gap-3">
                <div>
                  <div className="small text-muted">Avg</div>
                  <div className="fw-semibold">
                    {fmtMin(overview?.avg_hold_age_min)}
                  </div>
                </div>
                <div>
                  <div className="small text-muted">Median</div>
                  <div className="fw-semibold">
                    {fmtMin(overview?.p50_hold_age_min)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="card shadow-sm mb-3">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <div className="fw-semibold">Weekly Rejection Rate</div>
          <div className="small text-muted">
            {trend?.length ? `${trend.length} weeks` : "No data"}
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-lg-8">
              <LineChart
                values={(trend || []).map(
                  (t) => Number(t.rejection_rate_pct) || 0
                )}
              />
            </div>
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-2">Weeks</div>
              <div className="d-flex flex-wrap gap-2">
                {(trend || []).map((t, i) => (
                  <span key={i} className="badge bg-light text-dark border">
                    {niceDay(t.week_start)}–{niceDay(t.week_end)} •{" "}
                    {fmtPct(t.rejection_rate_pct, 1)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* By Partner (Top 10 by negatives = rejected + hold) */}
      <div className="card shadow-sm mb-3">
        <div className="card-header bg-white d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div className="fw-semibold">By Partner (Top 10 by negatives)</div>
          <div className="small text-muted">Click a row to copy partner ID</div>
        </div>

        <div className="card-body">
          {byPartner?.length > 0 && (
            <div className="alert alert-light border d-flex align-items-center justify-content-between mb-3">
              <div>
                <div className="small text-muted">
                  Worst performer (this range)
                </div>
                <div className="fw-semibold">
                  {byPartner[0].companyname || `#${byPartner[0].partner_id}`}
                </div>
              </div>
              <div className="d-flex gap-2">
                <span className="em-chip">
                  Assigned: {byPartner[0].assigned}
                </span>
                <span className="em-chip">
                  Rejected: {byPartner[0].rejected_count}
                </span>
                <span className="em-chip">Hold: {byPartner[0].hold_count}</span>
                <span className="em-chip">
                  Negatives: {byPartner[0].negative_count}
                </span>
              </div>
            </div>
          )}

          {byPartner.length === 0 ? (
            <div className="text-muted">No partner data.</div>
          ) : (
            <div className="list-group list-group-flush">
              {byPartner.map((p) => (
                <button
                  key={p.partner_id}
                  type="button"
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    navigator.clipboard?.writeText(String(p.partner_id));
                    toast?.info("Copied", `Partner ID ${p.partner_id}`);
                  }}
                  title="Click to copy Partner ID"
                >
                  <div className="d-flex justify-content-between align-items-baseline">
                    <div className="fw-semibold text-truncate me-3">
                      {p.companyname || `#${p.partner_id}`}
                    </div>
                    <div className="small text-muted">
                      Negatives: {p.negative_count} • Assigned: {p.assigned}
                    </div>
                  </div>

                  <div className="mt-2">
                    <SplitBar
                      assigned={Number(p.assigned || 0)}
                      rejected={Number(p.rejected_count || 0)}
                      holds={Number(p.hold_count || 0)}
                    />
                    <div className="d-flex gap-2 mt-2">
                      <span className="em-chip">
                        Rejected: {p.rejected_count}
                      </span>
                      <span className="em-chip">Hold: {p.hold_count}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Geography */}
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <div className="fw-semibold">
            By {geoGroup === "state" ? "State" : "Postcode"}
          </div>
        </div>
        <div className="card-body">
          {byGeo.length === 0 ? (
            <div className="text-muted">No geography data.</div>
          ) : (
            <div className="row g-3">
              {byGeo.slice(0, 12).map((g, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-4">
                  <HBar
                    label={g.key || "Unknown"}
                    sublabel={
                      g.rejected_count != null
                        ? `Rejected: ${g.rejected_count}`
                        : ""
                    }
                    pct={g.rejection_rate_pct}
                    assigned={g.assigned}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stats = () => {
  const toast = useToast();

  // Tabs
  const [tab, setTab] = React.useState("rejects"); // rejects | postcodes | companies | monthview

  // Shared helpers
  const thisYear = new Date().getFullYear();
  const [year, setYear] = React.useState(thisYear);

  // ====== Tab 1: Rejections (your existing block) ======
  // Keep your existing Rejections code inside a small component for clarity
  const TabRejects = () => {
    // (reuse your earlier “Rejections” component code here unchanged)
    // If you want, keep the same state and API calls you already wired up.
    return (
      <div>
        {/* ... your existing Rejections content ... */}
        <StatsRejections />{" "}
      </div>
    );
  };

  // ====== Tab 2: Postcodes (Jan→Dec matrix) ======
  const TabPostcodes = () => {
    const [stateFilter, setStateFilter] = React.useState("ALL");
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const months = Array.from(
      { length: 12 },
      (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`
    );

    const states = React.useMemo(() => {
      const s = new Set(rows.map((r) => r.state));
      return Array.from(s);
    }, [rows]);

    const dataMap = React.useMemo(() => {
      const m = new Map();
      rows.forEach((r) => m.set(`${r.state}|${r.ym}`, r.assigned));
      return m;
    }, [rows]);

    const load = async () => {
      setLoading(true);
      try {
        const r = await apiService.getPostcodeMonthly(year, stateFilter);
        setRows(Array.isArray(r) ? r : []);
      } catch (e) {
        console.error(e);
        toast.error("Failed", "Could not load postcode monthly.");
      } finally {
        setLoading(false);
      }
    };

    React.useEffect(() => {
      load();
    }, [year, stateFilter]); // eslint-disable-line

    return (
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex align-items-end gap-3 flex-wrap">
          <div>
            <label className="form-label small mb-1">Year</label>
            <input
              type="number"
              className="form-control"
              value={year}
              onChange={(e) => setYear(Number(e.target.value || thisYear))}
              style={{ width: 110 }}
            />
          </div>
          <div>
            <label className="form-label small mb-1">State</label>
            <select
              className="form-select"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="ALL">ALL</option>
              <option value="NSW">NSW</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="SA">SA</option>
              <option value="WA">WA</option>
              <option value="TAS">TAS</option>
              <option value="ACT">ACT</option>
              <option value="NT">NT</option>
            </select>
          </div>
          <button
            className="btn btn-outline-primary ms-auto"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>State</th>
                {months.map((m) => (
                  <th key={m}>
                    {new Date(m + "-01").toLocaleString("en-AU", {
                      month: "short",
                    })}
                  </th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {states.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center text-muted py-4">
                    No data.
                  </td>
                </tr>
              ) : (
                states.map((s) => {
                  const rowTotal = months.reduce(
                    (acc, ym) => acc + (Number(dataMap.get(`${s}|${ym}`)) || 0),
                    0
                  );
                  return (
                    <tr key={s}>
                      <td className="fw-semibold">{s}</td>
                      {months.map((ym) => (
                        <td key={ym}>
                          {Number(dataMap.get(`${s}|${ym}`)) || 0}
                        </td>
                      ))}
                      <td className="fw-semibold">
                        {rowTotal.toLocaleString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ====== Tab 3: Companies (YTD accepted/rejected) ======
  const PartnerLeadsModal = ({ partner, onClose }) => {
    const [assigned, setAssigned] = React.useState([]);
    const [rejected, setRejected] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
      setLoading(true);
      Promise.all([
        apiService.getCustomerAssignment(partner.partner_id),
        apiService.getCustomerUnassignment(partner.partner_id),
      ])
        .then(([a, r]) => {
          setAssigned(Array.isArray(a) ? a : []);
          setRejected(Array.isArray(r) ? r : []);
        })
        .catch((e) => console.error(e))
        .finally(() => setLoading(false));
    }, [partner.partner_id]);

    const exportCSV = () => {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const headers = ["Type", "First Name", "Last Name", "Email", "Postcode", "Company", "Reason", "Date"];
      const toRow = (type, l) => [
        esc(type),
        esc(l.firstname),
        esc(l.lastname),
        esc(l.email),
        esc(l.postcode),
        esc(l.companyname),
        esc(l.reason ?? ""),
        esc(l.date ? new Date(l.date).toLocaleDateString("en-AU") : ""),
      ].join(",");
      const csvRows = [
        headers.join(","),
        ...assigned.map((l) => toRow("Accepted", l)),
        ...rejected.map((l) => toRow("Rejected", l)),
      ];
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(partner.companyname || `partner-${partner.partner_id}`).replace(/\s+/g, "-")}-leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div
        className="modal d-block"
        tabIndex="-1"
        style={{ background: "rgba(0,0,0,0.5)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {partner.companyname || `#${partner.partner_id}`} — Leads
              </h5>
              <div className="ms-auto d-flex gap-2 me-2">
                {!loading && (assigned.length > 0 || rejected.length > 0) && (
                  <button className="btn btn-sm btn-success" onClick={exportCSV}>
                    Export CSV
                  </button>
                )}
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="text-center py-4 text-muted">Loading leads…</div>
              ) : (
                <>
                  <h6 className="fw-semibold mb-2">
                    Accepted ({assigned.length})
                  </h6>
                  {assigned.length === 0 ? (
                    <p className="text-muted small">No accepted leads.</p>
                  ) : (
                    <div className="table-responsive mb-4">
                      <table className="table table-sm table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Postcode</th>
                            <th>Company</th>
                            <th>Date Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assigned.map((l, i) => (
                            <tr key={i}>
                              <td>{l.firstname} {l.lastname}</td>
                              <td>{l.email}</td>
                              <td>{l.postcode}</td>
                              <td>{l.companyname}</td>
                              <td>{l.date ? new Date(l.date).toLocaleDateString("en-AU") : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h6 className="fw-semibold mb-2">
                    Rejected ({rejected.length})
                  </h6>
                  {rejected.length === 0 ? (
                    <p className="text-muted small">No rejected leads.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Postcode</th>
                            <th>Company</th>
                            <th>Reason</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rejected.map((l, i) => (
                            <tr key={i}>
                              <td>{l.firstname} {l.lastname}</td>
                              <td>{l.email}</td>
                              <td>{l.postcode}</td>
                              <td>{l.companyname}</td>
                              <td>{l.reason}</td>
                              <td>{l.date ? new Date(l.date).toLocaleDateString("en-AU") : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TabCompanies = () => {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedPartner, setSelectedPartner] = React.useState(null);

    const load = async () => {
      setLoading(true);
      try {
        const r = await apiService.getCompanyYear(year);
        setRows(Array.isArray(r) ? r : []);
      } catch (e) {
        console.error(e);
        toast.error("Failed", "Could not load company stats.");
      } finally {
        setLoading(false);
      }
    };

    React.useEffect(() => {
      load();
    }, [year]); // eslint-disable-line

    return (
      <>
        {selectedPartner && (
          <PartnerLeadsModal
            partner={selectedPartner}
            onClose={() => setSelectedPartner(null)}
          />
        )}
        <div className="card shadow-sm">
          <div className="card-header bg-white d-flex align-items-end gap-3">
            <div>
              <label className="form-label small mb-1">Year</label>
              <input
                type="number"
                className="form-control"
                value={year}
                onChange={(e) => setYear(Number(e.target.value || thisYear))}
                style={{ width: 110 }}
              />
            </div>
            <button
              className="btn btn-outline-primary ms-auto"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Company</th>
                  <th className="text-end">Assigned</th>
                  <th className="text-end">Accepted</th>
                  <th className="text-end">Rejected</th>
                  <th className="text-end">Hold</th>
                  <th className="text-end">Reject %</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No data.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.partner_id}
                      style={{ cursor: "pointer" }}
                      title="Click to view & export leads"
                      onClick={() => setSelectedPartner(r)}
                    >
                      <td className="text-primary text-decoration-underline">
                        {r.companyname || `#${r.partner_id}`}
                      </td>
                      <td className="text-end">
                        {Number(r.assigned || 0).toLocaleString()}
                      </td>
                      <td className="text-end">
                        {Number(r.accepted || 0).toLocaleString()}
                      </td>
                      <td className="text-end">
                        {Number(r.rejected_count || 0).toLocaleString()}
                      </td>
                      <td className="text-end">
                        {Number(r.hold_count || 0).toLocaleString()}
                      </td>
                      <td className="text-end">
                        {Number(r.rejection_rate_pct || 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  // ====== Tab 4: Month View (bucket view like screenshot) ======
  const TabMonthView = () => {
    const [stateFilter, setStateFilter] = React.useState("VIC");
    const [from, setFrom] = React.useState(`${year}-07-01`);
    const [to, setTo] = React.useState(`${year}-08-31`);
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const order = ["None", "One", "Two", "Three", "Four", "Five+"];

    const load = async () => {
      setLoading(true);
      try {
        const r = await apiService.getMonthView(stateFilter, from, to);
        setRows(Array.isArray(r) ? r : []);
      } catch (e) {
        console.error(e);
        toast.error("Failed", "Could not load month view.");
      } finally {
        setLoading(false);
      }
    };

    React.useEffect(() => {
      load();
    }, [stateFilter, from, to]); // eslint-disable-line

    const map = new Map(rows.map((r) => [r.bucket, r.cnt]));

    return (
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex align-items-end gap-3 flex-wrap">
          <div>
            <label className="form-label small mb-1">Select State</label>
            <select
              className="form-select"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="ALL">ALL</option>
              <option value="NSW">NSW</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="SA">SA</option>
              <option value="WA">WA</option>
              <option value="TAS">TAS</option>
              <option value="ACT">ACT</option>
              <option value="NT">NT</option>
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Select from date</label>
            <input
              type="date"
              className="form-control"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label small mb-1">Select to date</label>
            <input
              type="date"
              className="form-control"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button
            className="btn btn-outline-primary ms-auto"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table mb-0">
            <thead className="table-light">
              <tr>
                <th>Assigned to *no of partners</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {order.map((label) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{Number(map.get(label) || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ====== Tabs shell ======
  return (
    <div className="container-fluid px-0">
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "rejects" ? "active" : ""}`}
            onClick={() => setTab("rejects")}
          >
            Rejections
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "postcodes" ? "active" : ""}`}
            onClick={() => setTab("postcodes")}
          >
            Postcodes (Jan–Dec)
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "companies" ? "active" : ""}`}
            onClick={() => setTab("companies")}
          >
            Companies (YTD)
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "monthview" ? "active" : ""}`}
            onClick={() => setTab("monthview")}
          >
            Month View
          </button>
        </li>
      </ul>

      {tab === "rejects" && <TabRejects />}
      {tab === "postcodes" && <TabPostcodes />}
      {tab === "companies" && <TabCompanies />}
      {tab === "monthview" && <TabMonthView />}
    </div>
  );
};

// --- Admin: Update Password page ---
const UpdatePasswordPage = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const isPrivileged = ALLOWED_ROLES.includes(user?.type); // ["admin","sales"]

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!loginId.trim() || !password.trim()) {
      toast.error("Missing data", "Login ID and new password are required.");
      return;
    }

    if (password !== confirm) {
      toast.error(
        "Password mismatch",
        "New password and confirmation do not match."
      );
      return;
    }

    try {
      setSaving(true);
      await apiService.updatePassword({
        id: user.id,
        password,
      });
      toast.success(
        "Password updated",
        `Password updated for ${loginId.trim()}`
      );
      // leave loginId so they can re-use, just clear the password fields
      setPassword("");
      setConfirm("");
    } catch (err) {
      console.error(err);
      toast.error(
        "Update failed",
        err?.message || "Unable to update password. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isPrivileged) {
    return (
      <div className="container-fluid px-0">
        <div className="alert alert-warning mt-3">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      <h2 className="fw-bold fs-4 mb-3">Update User Password</h2>

      <div className="card shadow-sm">
        <div className="card-body">
          <form className="row g-3" onSubmit={handleSubmit} autoComplete="off">
            <div className="col-md-4">
              <label className="form-label">Login ID</label>
              <input
                className="form-control"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="Username / email / login id"
              />
              <div className="form-text">
                This should match the login ID used on the login screen.
              </div>
            </div>

            <div className="col-md-4">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                className="form-control"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main Layout Component
const Layout = () => {
  const [activeTab, setActiveTab] = useState("quotes");
  const { user } = useAuth();

  useEffect(() => {
    if (
      user?.type === "partner" &&
      (activeTab === "partners" || activeTab === "stats")
    ) {
      setActiveTab("quotes");
    }
  }, [user?.type]); // Only watch user.type, not activeTab

  const renderContent = () => {
    switch (activeTab) {
      case "quotes":
        return <QuotesList />;
      case "partners":
        return <PartnersList />;
      case "stats":
        return <Stats />;
      case "password":
        return <UpdatePasswordPage />;
      default:
        return <QuotesList />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">{renderContent()}</main>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Layout /> : <Login />;
};

// Root App with Auth Provider
const AppRoot = () => {
  return (
    /*  <AuthProvider>
      <App />
    </AuthProvider>
    */
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default AppRoot;
